// English, the site's source language. Every other file in this folder
// translates these same keys, using the wording of that language's App Store
// listing and the apps' own labels. A key a translation leaves out shows in
// English.
//
// {placeholders} are filled in by the page: {who} is set in the accent colour,
// {app} is an app's name, {link} is a link.

const en = {
    tagline: 'See who is speaking',

    topbar: {
        mainNav: 'Main',
        menu: 'Menu',
        getStarted: 'Get started',
        toLight: 'Switch to light theme',
        toDark: 'Switch to dark theme',
        language: 'Language',
        languageMenu: 'Choose a language'
    },

    nav: {
        how: 'How it works',
        apps: 'The apps',
        story: 'Story',
        pricing: 'Pricing',
        guide: 'User guide'
    },

    footer: {
        label: 'Footer',
        getStarted: 'Get started',
        guide: 'User guide',
        story: 'Our story',
        privacy: 'Privacy',
        terms: 'Terms of Use'
    },

    // shown on a page whose content is in English, to a reader who chose another language
    englishOnly: 'This page is available in English only.',

    // the chrome of the guide pages
    docs: {
        subnav: 'User guide',
        getStarted: 'Get started',
        guide: 'Full guide',
        contents: 'Contents'
    },

    // Descriptions of the App Store screenshots, named as in screens.js. Other
    // languages show these in place of the site's English screenshots, which
    // keep their own descriptions in screens.js.
    screens: {
        hostAuto: 'CaptiVision Host in Auto view. The student asking a question is framed in amber, and every caption line starts with the name of the person speaking.',
        hostTwoFeed: 'Two-Feed view: a sign language interpreter on one side, and on the other the lecturer framed in amber. Named captions run along the bottom.',
        hostGrid: 'Grid view: every camera at once, with the speaker framed in amber and named captions along the bottom.',
        hostSpeakers: 'The Speakers list: each person who has spoken, with a photo, the one speaking now marked, and how far to zoom in on the speaker.',
        hostCaptions: 'The Captions sheet: captions on the stage, the text size with a preview line, how many lines stay on screen, and captioning the interpreter.',
        hostSpeech: 'The Speech recognition sheet: the engine, the Whisper models to download, and whether captions are in the language spoken or translated into English.',
        hostSessionLog: 'The Session log: the session starting, cameras joining and speakers being named, each with its time.',
        hostAddCamera: 'The Add a client sheet: a code to scan, the session code, the time left to join, how many cameras have joined, the pairing link and the switches for joining.',
        cameraStart: 'The CaptiVision Client start screen: scan the host\'s code, paste a pairing link, the sessions found on this network, and what the app does with your phone.',
        cameraScan: 'The scanner pointed at the pairing code on the iPad.',
        cameraPasteLink: 'Pasting a pairing link, with a button to join.',
        cameraJoin: 'The join screen: this camera\'s name, its role, what this device will do, and Start streaming.',
        cameraDnd: 'The prompt to turn on Do Not Disturb, so that a call does not cut off the camera.',
        cameraDndSteps: 'The two steps to turn on Do Not Disturb from Control Center, with the app waiting for it to come on.',
        cameraLive: 'A phone streaming live, with its connection, frame rate, delay and battery, and buttons for zoom, the microphone, the screen, the camera and leaving.',
        cameraMicOnly: 'A phone used as a microphone only: the camera is off, the microphone is on, and the phone can be locked and passed around.'
    },

    home: {
        simulated: 'Screen images on this page are simulated. They were captured in the iOS Simulator, with recorded lecture video standing in for live cameras.',

        hero: {
            eyebrow: 'Live captions and video for Deaf and hard of hearing people',
            title: 'See {who} is speaking.',
            who: 'who',
            subtitle: 'Not just what was said.',
            lede: 'Captions tell you what was said. In a room full of people, you also need to know who said it. CaptiVision turns a few iPhones into cameras around the room. Your iPad or Mac spots the person talking, lights them up, and writes live captions with their name in front.',
            getStarted: 'Get started',
            seeHow: 'See how it works',
            facts: ['iPhone, iPad and Mac', 'Works without internet', 'Nothing leaves the room']
        },

        showcase: 'Auto view in CaptiVision Host. The camera follows Leo as he asks a question, and every caption starts with a name.',

        why: {
            title: 'Captions alone leave out half the conversation.',
            body: [
                'When several people talk, a Deaf or hard of hearing person has to scan the room to find who is speaking. By the time they find them, the first words are gone, and the caption on screen could belong to anyone.',
                'CaptiVision shows them where to look before the words arrive, and puts a name on every line. A student can tell that a classmate asked the question, not the teacher, and answer the right person.'
            ]
        },

        how: {
            kicker: 'How it works',
            title: 'Ready in the five minutes before class.',
            steps: [
                {
                    title: 'Place the phones',
                    body: 'Stand a few iPhones around the room, facing the people who will talk. Borrowed phones work too.'
                },
                {
                    title: 'Scan the code',
                    body: 'Start a session on your iPad or Mac. Each phone scans the code on screen and joins. No accounts and no network settings.'
                },
                {
                    title: 'Follow along',
                    body: 'The iPad or Mac lights up whoever is talking and writes captions with their name, as they speak.'
                }
            ]
        },

        features: {
            kicker: 'What you get',
            title: 'Built for the way rooms really talk.',
            items: [
                {
                    title: 'The speaker lights up',
                    body: 'A bright outline marks the person talking, on every camera that sees them. It holds steady through short pauses and does not flicker between people.'
                },
                {
                    title: 'A name on every caption',
                    // the sample caption is set in mono between `body` and `after`
                    body: 'Lines read',
                    quote: { name: 'Tommy', text: 'What are spheres?' },
                    after: 'Give someone a name once and every line they already said updates too.'
                },
                {
                    title: 'The view follows the talk',
                    body: 'Show every camera at once, or let the iPad or Mac switch to whoever is speaking, the way a TV director would.'
                },
                {
                    title: 'Captions sized for the back row',
                    body: 'Make captions as large as the room needs and choose how many lines stay on screen, so a glance away never loses the thread.'
                },
                {
                    title: 'Point to fix it',
                    body: 'Someone hidden behind a microphone? Draw a box around them on the iPad or Mac and they become the speaker straight away.'
                },
                {
                    title: 'Keep a record',
                    body: 'Record every camera with its captions, plus a written transcript with names and times. Useful as study notes.'
                }
            ]
        },

        // the carousel of close-ups, keyed by the image each one describes
        closer: {
            kicker: 'Inside CaptiVision Host',
            title: 'Take a closer look.',
            label: 'Features of CaptiVision Host',
            previous: 'Previous',
            next: 'Next',
            items: {
                'pairing-code-modal': {
                    title: 'Add a camera in seconds.',
                    body: 'Each phone scans the code on the iPad or Mac and joins. There are no accounts to create and no network settings to change.',
                    alt: 'The Add a client sheet: a code to scan, the session code, the time left to join, how many cameras have joined, and the pairing link.'
                },
                'camera-and-microphone-feed': {
                    title: 'See who is speaking.',
                    body: 'The camera showing the person talking gets a bright border, and the view can follow them from camera to camera. Every feed shows its battery and sound level.',
                    alt: 'A camera feed with a bright amber border. The lecturer is framed and named, and the feed shows its battery, frame rate and delay.'
                },
                'speakers-modal': {
                    title: 'Know who said what.',
                    body: 'One person keeps one name, even when they turn away, walk or are filmed by another camera. Name someone once and everything they already said updates too.',
                    alt: 'The Speakers sheet: three people with their photos, one marked Speaking now, and how far to zoom in on the speaker, from 1× to 3× or Auto.'
                },
                'captions-modal': {
                    title: 'Captions sized for the back row.',
                    body: 'Every line reads [Name]: what they said, in the size you choose. When an interpreter speaks for someone who signs, their words are captioned as Interpreter.',
                    alt: 'The Captions sheet: a switch to show captions on the stage, a text size slider with a preview line, how many lines to show, and a switch to caption the interpreter\'s voice.'
                },
                'this-device-feed': {
                    title: 'The iPad or Mac joins in.',
                    body: 'Its own camera and microphone can be a feed too, with buttons to mute, turn off the camera, choose a microphone or leave.',
                    alt: 'The feed from this device\'s own camera, filming a lecture hall, with buttons for the microphone, the camera and Leave.'
                },
                'microphone-only-feed': {
                    title: 'A microphone to pass around.',
                    body: 'A phone can be a microphone only, passed from hand to hand. Passthrough plays it through the speakers of the iPad or Mac.',
                    alt: 'A microphone-only feed with a Passthrough button, which plays this microphone through the device\'s speakers.'
                },
                'speech-recognition-modal': {
                    title: 'Speech recognition on the device.',
                    body: 'Captions are made on the iPad or Mac itself, with Apple\'s on-device speech or a Whisper model. Whisper can caption in the language spoken, or translate it into English.',
                    alt: 'The Speech recognition sheet: Whisper chosen as the engine, three Whisper models to download, and a choice between captions in the language spoken or translated into English.'
                },
                'help-guide-popup': {
                    title: 'Choose what the room sees.',
                    body: 'Grid shows every camera. Auto follows whoever is speaking. Two-Feed keeps the interpreter beside the speaker. A built-in guide walks through each control.',
                    alt: 'A help guide card, 1 of 8, pointing at the view buttons: Grid, Auto and Two-Feed.'
                },
                'session-log-modal': {
                    title: 'A record of every change.',
                    body: 'The session log notes every camera that joins and every speaker who is named, with the time. Record every camera with its captions, plus a transcript with names and times.',
                    alt: 'The Session log: the session starting, cameras joining, a camera being renamed and speakers being named, each with its time.'
                }
            }
        },

        interpreter: {
            kicker: 'Two-Feed view',
            title: 'The interpreter stays on screen. Always.',
            body: [
                'Pin the sign language interpreter\'s camera to one side. The other side follows whoever is talking, so no one has to choose between watching the signer and watching the speaker.',
                'The interpreter\'s camera is never cut away from, and it is the last to lose quality if the network gets busy.'
            ]
        },

        languages: {
            title: 'In your language, on every device.',
            body: [
                'CaptiVision Host and CaptiVision Client are available in 11 languages, and each follows the language of the device it runs on. A borrowed phone stays in its owner\'s language.',
                'Captions follow the room too. Whisper captions in the language being spoken, or translates it into English as people talk.'
            ],
            list: 'Available in'
        },

        micOnly: {
            kicker: 'Microphone only',
            title: 'No microphone in the room?',
            subtitle: 'Pass a phone around.',
            body: [
                'Turn a phone\'s camera off and it becomes a wireless microphone. Hand it to whoever is speaking, down a row of seats or across a meeting table, and every word reaches the captions.',
                'It keeps listening while the phone is locked, so it can go from hand to hand without anyone touching the screen. Passthrough plays it through the speakers of the iPad or Mac, so the room hears the speaker too.'
            ]
        },

        privacy: {
            title: 'It never leaves the room.',
            body: [
                'Video, sound, captions and names stay on your iPad or Mac and your own Wi-Fi. Nothing is sent to the internet, to us, or to anyone else. Once set up, it works with no internet connection at all.',
                'The phones film and listen only while they are in a session, and keep nothing when they leave. Every camera shows when the iPad or Mac is recording.'
            ],
            more: 'Read the privacy policy'
        },

        uses: {
            kicker: 'Where it helps',
            title: 'Anywhere more than two people talk.',
            items: [
                {
                    title: 'Classrooms',
                    body: 'One phone faces the students, one faces the board. Deaf students see which classmate is speaking and take home the transcript as notes.'
                },
                {
                    title: 'Meetings and town halls',
                    body: 'Phones around the table cover everyone. The interpreter stays on screen beside whoever has the floor.'
                },
                {
                    title: 'Panels and events',
                    body: 'When a speaker\'s face is hidden by a microphone, the host draws a box around them and the captions stay correct.'
                }
            ]
        },

        story: {
            title: 'The Story behind CaptiVision',
            body: 'How the idea for CaptiVision was born in a classroom at the University of Texas at Austin.',
            more: 'Read the story'
        },

        apps: {
            kicker: 'The apps',
            title: 'Two apps. One room.',
            lede: 'Phones are the eyes and ears. The iPad or Mac does the thinking.',
            badge: 'Download {app} on the App Store',
            icon: '{app} app icon',
            client: {
                platform: ['iPhone', 'iOS 15.6 or later'],
                price: 'Free',
                body: 'Turns any iPhone into a camera for the room. It films, listens, and sends everything straight to the iPad or Mac. It saves nothing on the phone and leaves nothing behind.',
                ticks: [
                    'Joins by scanning a code on the iPad or Mac',
                    'Keeps faces bright, even in front of a window',
                    'Darkens its own screen to stay cool in long sessions',
                    'The phone\'s owner can mute it or leave at any time',
                    'Works as a wireless microphone, even when the phone is locked'
                ]
            },
            host: {
                platform: ['iPad and Mac', 'iPadOS 17 or macOS 14'],
                price: 'Free for a week',
                body: 'The iPad or Mac that runs the session. It watches every camera, works out who is talking, writes the captions, and records when you ask it to.',
                ticks: [
                    'Up to six cameras in one room',
                    'Adjust any camera\'s zoom and role from the iPad or Mac',
                    'Rename speakers at any time',
                    'Captions are made on the iPad or Mac, not in the cloud',
                    'Passthrough plays any phone\'s microphone through a connected speaker'
                ]
            }
        },

        closing: {
            title: 'Ready for your next class or meeting?',
            getStarted: 'Get started',
            guide: 'Read the full guide'
        }
    },

    // where a pairing code lands when it is scanned without CaptiVision Client installed
    // The pricing page. The table's {monthly} and {yearly} are the US prices from the
    // config, written the reader's way; `region` says the App Store charges local
    // prices.
    pricing: {
        kicker: 'Pricing',
        title: 'Free to try. Simple after that.',
        lede: 'The Client app is always free. The Host app is free for your first week, then needs CaptiVision Pro.',
        tiers: [
            {
                name: 'CaptiVision Client',
                note: 'For every iPhone in the room',
                price: 'Free',
                blurb: 'Always free, on as many phones as you like.'
            },
            {
                name: 'CaptiVision Host',
                note: 'Your first week',
                price: 'Free',
                blurb: 'Everything included for seven days from the day you first open it.'
            },
            {
                name: 'CaptiVision Pro, monthly',
                note: 'For the Host app',
                price: '{monthly}',
                per: '/ month',
                blurb: 'Starts with a one-week free trial.'
            },
            {
                name: 'CaptiVision Pro, yearly',
                note: 'For the Host app',
                price: '{yearly}',
                per: '/ year',
                blurb: 'About two months free compared with paying monthly.'
            }
        ],
        regionLabel: 'Prices vary by region',
        region: 'These are the prices in the United States, in US dollars. The App Store charges in your local currency, and the price can differ from one country or region to another. It shows you the exact price before you buy.',
        fine: 'Subscriptions are billed through your Apple Account and renew automatically until you cancel. Cancel any time from your Apple Account subscriptions, at least 24 hours before the end of the current period. See the {terms}.',
        terms: 'Terms of Use',

        // under the table, in the order they are read
        good: {
            title: 'Good to know',
            items: [
                {
                    title: 'Only the Host needs Pro',
                    body: 'CaptiVision Client is free on every phone. CaptiVision Pro is only needed on the iPad or Mac that runs the session.'
                },
                {
                    title: 'Your free week',
                    body: 'It starts the day you first open CaptiVision Host, and the Upgrade button shows how many days are left. A session that is already running when the week ends is never cut off.'
                },
                {
                    title: 'Already subscribed?',
                    body: 'If you subscribed on another iPad or Mac, use Restore on the plans screen.'
                },
                {
                    title: 'Cancel any time',
                    body: 'Apple handles your subscription. Manage or cancel it in Settings, under your name, then Subscriptions; on a Mac, in the App Store under your account. You keep Pro until the end of the period you paid for.'
                }
            ]
        },

        // over the two App Store badges at the foot of the page
        download: 'Download the apps'
    },

    join: {
        title: 'Join a Session',
        eyebrow: 'Join a session',
        heading: 'Get CaptiVision Client',
        lede: 'To join this captioning session from your iPhone, install CaptiVision Client from the App Store. Then scan the code on the host again.',
        store: 'View on the App Store',
        open: 'Already have CaptiVision Client? {link}.',
        openLink: 'Open it and join'
    }
}

export default en;
